// 적 스폰 관리자 클래스
class EnemySpawnManager {
  constructor(scene, camera, player) {
    this.scene = scene;
    this.camera = camera;
    this.player = player;

    // 스폰된 적들 관리
    this.enemies = [];

    // 스폰 관련 설정 - EnemySpawnConfig에서 가져옴
    this.config = EnemySpawnConfig.SPAWN;
    this.patternConfig = EnemySpawnConfig.PATTERNS;
    this.enemyTypes = EnemyTypesConfig;

    // 환경 오브젝트 참조
    this.environmentObjects = [];

    // 카메라 관련 정보
    this.cameraViewSize = GameConfig.CAMERA.VIEW_SIZE;

    // 동료 관리자 참조 (부채꼴 공격용 적 목록 동기화)
    this.companionManager = null;

    // 스폰 스케줄러 참조 (보스 등록용)
    this.spawnScheduler = null;
  }

  // 환경 오브젝트 설정 (SceneManager에서 호출)
  setEnvironmentObjects(objects) {
    this.environmentObjects = objects;
  }

  // 동료 관리자 설정 (EntitiesInitializer에서 호출)
  setCompanionManager(companionManager) {
    this.companionManager = companionManager;
  }

  // 스폰 스케줄러 설정 (SystemsInitializer에서 호출)
  setSpawnScheduler(spawnScheduler) {
    this.spawnScheduler = spawnScheduler;
  }

  // 적 업데이트 (매 프레임 호출)
  update(delta) {
    const playerPosition = this.player.getPosition();

    // 모든 적 업데이트
    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const enemy = this.enemies[i];

      if (enemy.isModelLoaded()) {
        enemy.update(delta, playerPosition);

        // 시체 상태이고 완전히 사라진 적 제거
        if (!enemy.isAlive && !enemy.isDying && !enemy.isCorpse) {
          this.enemies.splice(i, 1);
        }
      }
    }
  }

  // 플레이어 참조 설정
  setPlayer(player) {
    this.player = player;
    // 기존 적들에게도 플레이어 정보 전달
    this.enemies.forEach((enemy) => {
      if (enemy.setPlayer) {
        enemy.setPlayer(player);
      }
    });
  }

  // 카메라 밖 영역 계산
  getCameraBounds() {
    if (!this.camera || !this.player) return null;

    const playerPos = this.player.getPosition();

    // 카메라의 뷰 크기를 이용해 경계 계산
    const margin = this.config.CAMERA_MARGIN;

    return {
      left: playerPos.x - this.cameraViewSize - margin,
      right: playerPos.x + this.cameraViewSize + margin,
      top: playerPos.z - this.cameraViewSize - margin,
      bottom: playerPos.z + this.cameraViewSize + margin,
    };
  }

  // 카메라 밖인지 확인
  isOutsideCamera(x, z) {
    const bounds = this.getCameraBounds();
    if (!bounds) return false;

    return (
      x < bounds.left || x > bounds.right || z < bounds.top || z > bounds.bottom
    );
  }

  // 안전한 스폰 위치 찾기 (카메라 밖 + 플레이어로부터 적절한 거리)
  findSafeSpawnPosition(playerPos) {
    const maxAttempts = 20;
    let attempts = 0;

    while (attempts < maxAttempts) {
      // 플레이어 주변 원형 영역에서 랜덤 위치 생성
      const angle = Math.random() * Math.PI * 2;
      const distance =
        this.config.MIN_SPAWN_DISTANCE +
        Math.random() *
          (this.config.MAX_SPAWN_DISTANCE - this.config.MIN_SPAWN_DISTANCE);

      const x = playerPos.x + Math.cos(angle) * distance;
      const z = playerPos.z + Math.sin(angle) * distance;

      // 카메라 밖인지 확인
      if (this.isOutsideCamera(x, z)) {
        return { x, z };
      }

      attempts++;
    }

    // 안전한 위치를 찾지 못한 경우 강제로 카메라 밖에 배치
    const angle = Math.random() * Math.PI * 2;
    const distance = this.config.MAX_SPAWN_DISTANCE;
    return {
      x: playerPos.x + Math.cos(angle) * distance,
      z: playerPos.z + Math.sin(angle) * distance,
    };
  }

  // 1. 랜덤 단일 스폰
  async spawnRandom(enemyType) {
    if (!this.player) return null;

    const playerPos = this.player.getPosition();
    const spawnPos = this.findSafeSpawnPosition(playerPos);

    const enemy = await this.createEnemy(enemyType, spawnPos.x, spawnPos.z);

    return enemy;
  }

  // 2. 뭉태기 스폰 (한 지점 주변에 여러 마리)
  async spawnCluster(enemyType, count = null) {
    if (!this.player) return [];

    const playerPos = this.player.getPosition();
    const centerPos = this.findSafeSpawnPosition(playerPos);

    // 스폰할 개수 결정
    if (!count) {
      const clusterConfig = this.patternConfig.CLUSTER;
      count =
        Math.floor(
          Math.random() * (clusterConfig.SIZE_MAX - clusterConfig.SIZE_MIN + 1)
        ) + clusterConfig.SIZE_MIN;
    }

    // 병렬로 적 생성 (Promise.all 사용)
    const spawnPromises = [];

    for (let i = 0; i < count; i++) {
      // 중심점 주변 랜덤 위치
      const angle = Math.random() * Math.PI * 2;
      const distance = Math.random() * this.patternConfig.CLUSTER.RADIUS;

      const x = centerPos.x + Math.cos(angle) * distance;
      const z = centerPos.z + Math.sin(angle) * distance;

      spawnPromises.push(this.createEnemy(enemyType, x, z));
    }

    // 모든 적을 병렬로 생성
    const spawnedEnemies = (await Promise.all(spawnPromises)).filter(
      (enemy) => enemy !== null
    );

    return spawnedEnemies;
  }

  // 3. 보스 스폰 (보스 1마리 생성)
  async spawnBoss(enemyType) {
    if (!this.player || !this.spawnScheduler) return [];

    const playerPos = this.player.getPosition();
    const stageNumber = this.spawnScheduler.currentStage || 0;
    const bossCount = 1; // 보스는 항상 1마리만 생성

    const bossConfig = this.patternConfig.BOSS;

    Utils.log(
      `보스 스폰 시작: 스테이지 ${stageNumber + 1} (${bossCount}마리 생성)`
    );

    let spawnPos = null;
    let attempts = 0;
    const maxAttempts = 50; // 최대 시도 횟수

    // 보스 스폰 위치 찾기 (카메라 밖 + 적절한 거리)
    while (attempts < maxAttempts) {
      // 플레이어 중심 원형 영역에서 위치 선택
      // 보스는 일반 적보다 더 멀리서 생성되도록 별도의 최소 거리 사용
      const bossMinDistance =
        bossConfig.MIN_SPAWN_DISTANCE || this.config.MIN_SPAWN_DISTANCE;
      const angle = Math.random() * Math.PI * 2; // 랜덤 각도
      const distance =
        bossMinDistance +
        Math.random() * (bossConfig.SPACING_RADIUS - bossMinDistance);

      const x = playerPos.x + Math.cos(angle) * distance;
      const z = playerPos.z + Math.sin(angle) * distance;

      // 카메라 밖인지 확인
      if (this.isOutsideCamera(x, z)) {
        spawnPos = { x, z };
        break;
      }

      attempts++;
    }

    // 적절한 위치를 찾지 못한 경우 보스 최소 거리를 사용하여 생성
    if (!spawnPos) {
      const bossMinDistance =
        bossConfig.MIN_SPAWN_DISTANCE || this.config.MIN_SPAWN_DISTANCE;
      const angle = Math.random() * Math.PI * 2;
      // 보스 최소 거리에서 최대 거리 사이의 중간값 사용
      const distance = (bossMinDistance + bossConfig.SPACING_RADIUS) / 2;
      spawnPos = {
        x: playerPos.x + Math.cos(angle) * distance,
        z: playerPos.z + Math.sin(angle) * distance,
      };
      Utils.log(
        `보스 위치를 찾지 못해 기본 위치 사용: (${spawnPos.x.toFixed(
          1
        )}, ${spawnPos.z.toFixed(1)})`,
        "warn"
      );
    }

    // 보스 생성
    const enemy = await this.createEnemy(enemyType, spawnPos.x, spawnPos.z);

    // 카메라 회전 효과 제거 (요청에 따라 보스 등장 시 회전하지 않음)

    Utils.log(
      `보스 스폰 완료: ${enemy ? 1 : 0}마리 생성 (스테이지 ${stageNumber + 1})`
    );

    return enemy ? [enemy] : [];
  }

  // 적 생성 및 초기화
  async createEnemy(enemyType, x, z) {
    try {
      // WARRIOR 타입일 때는 보스 클래스 사용
      const enemy =
        enemyType === "WARRIOR"
          ? new WarriorEnemy(this.scene, x, 0, z)
          : new Enemy(this.scene, x, 0, z, enemyType);
      await enemy.init();
      enemy.setEnvironmentObjects(this.environmentObjects);

      // 스테이지별 스탯 증가 적용
      if (this.spawnScheduler) {
        const currentStage = this.spawnScheduler.currentStage;
        const scaling = EnemySpawnConfig.getStageScaling(currentStage);

        // 체력과 공격력에 스테이지 배율 적용
        enemy.maxHealth *= scaling;
        enemy.health *= scaling;
        enemy.attackDamage *= scaling;
        // enemy.moveSpeed *= scaling;

        // 보스의 경우 체력바 업데이트
        if (enemy.healthBar) {
          enemy.healthBar.updateHealth(enemy.health, enemy.maxHealth);
        }

        Utils.log(
          `적 생성 (${enemyType}, 스테이지 ${
            currentStage + 1
          }, 배율 x${scaling.toFixed(2)}): HP ${enemy.health.toFixed(
            0
          )}, ATK ${enemy.attackDamage.toFixed(1)}`
        );
      }

      this.enemies.push(enemy);

      // 보스라면 스케줄러에 등록
      if (enemyType === "WARRIOR" && this.spawnScheduler) {
        this.spawnScheduler.registerBoss(enemy);
      }

      // 플레이어와 동료들에게 적 목록 동기화
      this.player.setEnemies(this.enemies);
      if (this.companionManager) {
        this.companionManager.setEnemies(this.enemies);
      }

      return enemy;
    } catch (error) {
      return null;
    }
  }

  // 스폰 패턴 이름으로 실행
  async executeSpawnPattern(patternName, enemyType, ...args) {
    try {
      if (!patternName || !enemyType) {
        return null;
      }

      switch (patternName.toLowerCase()) {
        case "random":
          // args는 받지만 현재는 사용하지 않음 (스테이지 번호는 createEnemy에서 spawnScheduler.currentStage로 접근)
          return await this.spawnRandom(enemyType);
        case "cluster":
          return await this.spawnCluster(enemyType, ...args);
        case "boss":
          // 보스 스폰: 보스 1마리 생성
          return await this.spawnBoss(enemyType);
        default:
          Utils.log(`알 수 없는 스폰 패턴: ${patternName}`, "warn");
          return null;
      }
    } catch (error) {
      Utils.log(`스폰 패턴 실행 오류: ${error.message}`, "error");
      return null;
    }
  }

  // 현재 상태 정보
  getStatus() {
    return {
      totalEnemies: this.enemies.length,
      aliveEnemies: this.enemies.filter((e) => e.isAliveCheck()).length,
      dyingEnemies: this.enemies.filter((e) => e.isDying).length,
      corpseEnemies: this.enemies.filter((e) => e.isCorpse).length,
      enemyTypes: this.enemies.reduce((acc, enemy) => {
        acc[enemy.enemyType] = (acc[enemy.enemyType] || 0) + 1;
        return acc;
      }, {}),
    };
  }

  // 모든 적 제거
  removeAllEnemies() {
    // 한국어 주석: 각 적의 dispose 메서드를 호출하여 완전히 정리
    this.enemies.forEach((enemy) => {
      if (enemy.dispose) {
        enemy.dispose(); // 모델, 데미지 숫자, 애니메이션 등 모두 정리
      } else if (enemy.model) {
        this.scene.remove(enemy.model); // dispose가 없으면 모델만이라도 제거
      }
    });
    this.enemies = [];

    // 플레이어와 동료들의 적 목록 업데이트
    if (this.player) {
      this.player.setEnemies([]);
    }
    if (this.companionManager) {
      this.companionManager.setEnemies([]);
    }
  }
}

// webpack 빌드를 위해 default export 추가
export default EnemySpawnManager;
